<template>
  <div class="intel-grid" role="grid" aria-label="Defense intel grid">
    <div
      v-for="cell in cells"
      :key="cell.key"
      class="intel-cell"
      :class="{
        fog: !cell.revealed,
        vacant: cell.revealed && !cell.unit && !cell.isGate,
        gate: cell.revealed && cell.isGate,
        occupied: cell.revealed && !!cell.unit,
        hero: cell.revealed && cell.isHero,
      }"
      :title="cell.title"
    >
      <template v-if="cell.revealed">
        <i
          v-if="cell.isGate"
          class="fas fa-dungeon cell-icon"
          aria-hidden="true"
        ></i>
        <i
          v-else-if="cell.unit"
          :class="[cell.unit.icon, 'cell-icon']"
          :style="{ color: cell.unit.color }"
          aria-hidden="true"
        ></i>
        <span v-if="cell.unit" class="cell-qty">{{ cell.unit.quantity }}</span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { GRID_SIZE, GATE_ROW, DEFAULT_GATE_COL } from "../utils/defenseFormation";
import { unitDisplay } from "../utils/unitDisplay";

const props = defineProps({
  formation: {
    type: Object,
    default: () => ({ gate_col: DEFAULT_GATE_COL, cells: [] }),
  },
  revealed: {
    type: Array,
    default: () => [],
  },
  race: {
    type: String,
    default: "",
  },
});

function cellKey(row, col) {
  return `${row},${col}`;
}

const revealedSet = computed(() => {
  const set = new Set();
  for (const entry of props.revealed || []) {
    if (Array.isArray(entry) && entry.length >= 2) {
      set.add(cellKey(entry[0], entry[1]));
    } else if (entry && typeof entry === "object") {
      set.add(cellKey(entry.row, entry.col));
    }
  }
  return set;
});

const occupiedMap = computed(() => {
  const map = new Map();
  const cells = Array.isArray(props.formation?.cells)
    ? props.formation.cells
    : [];
  for (const cell of cells) {
    map.set(cellKey(cell.row, cell.col), cell);
  }
  return map;
});

const gateCol = computed(() => {
  const n = Math.floor(Number(props.formation?.gate_col));
  if (!Number.isFinite(n) || n < 0 || n >= GRID_SIZE) return DEFAULT_GATE_COL;
  return n;
});

const garrisonHero = computed(() => {
  const raw = props.formation?.garrisonHero;
  if (!raw || typeof raw !== "object") return null;
  const heroId = Math.floor(
    Number(raw.heroId != null ? raw.heroId : raw.hero_id)
  );
  const row = Math.floor(Number(raw.row));
  const col = Math.floor(Number(raw.col));
  if (!Number.isFinite(heroId) || heroId < 1) return null;
  if (
    !Number.isFinite(row) ||
    !Number.isFinite(col) ||
    row < 0 ||
    row >= GRID_SIZE ||
    col < 0 ||
    col >= GRID_SIZE
  ) {
    return null;
  }
  return { heroId, row, col };
});

const cells = computed(() => {
  const list = [];
  const hero = garrisonHero.value;
  for (let row = 0; row < GRID_SIZE; row += 1) {
    for (let col = 0; col < GRID_SIZE; col += 1) {
      const key = cellKey(row, col);
      const revealed = revealedSet.value.has(key);
      const isGate = row === GATE_ROW && col === gateCol.value;
      const isHero =
        !!hero && !isGate && hero.row === row && hero.col === col;
      const raw = occupiedMap.value.get(key);
      let unit = null;
      if (isHero) {
        unit = {
          name: "Hero",
          icon: "fas fa-crown",
          color: "#f0d060",
          quantity: "Hero",
        };
      } else if (raw) {
        const display = unitDisplay(raw.unit_slug, props.race);
        unit = {
          ...display,
          quantity: Math.max(0, Math.floor(Number(raw.quantity) || 0)),
        };
      }
      let title = "Fog of War";
      if (revealed) {
        if (isGate) title = "City Gate";
        else if (isHero) title = "Hero";
        else if (unit) title = `${unit.name} × ${unit.quantity}`;
        else title = "Empty";
      }
      list.push({ key, row, col, revealed, isGate, isHero, unit, title });
    }
  }
  return list;
});
</script>

<style scoped>
.intel-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 3px;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;
}

.intel-cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  min-height: 0;
  background: #1a2030;
  border: 1px solid rgba(80, 90, 120, 0.35);
}

.intel-cell.fog {
  background: #2a3140;
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.55);
}

.intel-cell.vacant {
  background: rgba(20, 28, 42, 0.9);
  border-color: rgba(0, 246, 255, 0.18);
}

.intel-cell.gate {
  background: rgba(201, 180, 138, 0.18);
  border-color: #c9b48a;
  color: #c9b48a;
}

.intel-cell.occupied {
  background: rgba(8, 16, 28, 0.95);
  border-color: rgba(0, 246, 255, 0.35);
}

.intel-cell.hero {
  border-color: #f0d060;
  color: #f0d060;
}

.cell-icon {
  font-size: 0.7rem;
  line-height: 1;
}

.cell-qty {
  font-size: 0.58rem;
  font-family: monospace;
  color: #d8dee9;
  line-height: 1;
  margin-top: 1px;
}
</style>
